/**
 * The one text-based filter table (search-result-disclosure 04, extended by
 * filter-table-reuse 01).
 *
 * Search, the item list and the photo gallery all render this component and
 * supply their own rows, so the interaction never forks:
 *
 *   Open search filter · 2     scope: Items · (owner:Anna OR owner:Ben)
 *   | Owner | Anna (7)  Ben (4) … |
 *   | …                           |
 *   | — Display —                 |
 *   | Previews | show  hide       |
 *
 * The table owns open/closed state and the value-link rendering; the caller
 * owns the filter semantics, the counts and the summary. Values are OR-ed
 * within a row and AND-ed across rows (the caller's semantics).
 */

import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useT } from "../i18n";

export interface FilterValue {
  value: string;
  label: string;
  count?: number;
  selected: boolean;
  /** Stable test id; defaults to `<row.key>-<value>`. */
  testId?: string;
}

export interface FilterRow {
  key: string;
  label: string;
  values: FilterValue[];
  onSelect: (value: string) => void;
}

export interface DisplayFilterRow {
  key: string;
  label: string;
  shown: boolean;
  onChange: (shown: boolean) => void;
}

const ROW_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "120px 1fr" },
  gap: { xs: 0.25, md: 1.5 },
  alignItems: { md: "baseline" },
  px: 1.25,
  py: 0.75,
  borderTop: 1,
  borderColor: "divider",
} as const;

/**
 * The table's open/close, clear-all and value links are text links in a
 * sentence, so the WCAG 2.5.8 AA 24px minimum applies on both pointer types
 * instead of the 32/36/44px button targets. `data-control` is the explicit
 * contract the dense-control e2e audit reads; every other `button` and
 * `[role="button"]` must meet the theme's pointer-aware target.
 */
const LINK_SX = {
  p: 0,
  border: 0,
  bgcolor: "transparent",
  cursor: "pointer",
  font: "inherit",
  textAlign: "left",
  minHeight: 24,
} as const;

export function FilterTable({
  rows,
  displayRows = [],
  activeCount,
  summary,
  onClear,
}: {
  rows: readonly FilterRow[];
  displayRows?: readonly DisplayFilterRow[];
  /** Number of applied filters (excludes sort/mode rows). */
  activeCount: number;
  /** Closed-state summary; empty hides the line. */
  summary: string;
  onClear: () => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const active = activeCount > 0;

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
        <Box
          component="button"
          type="button"
          aria-expanded={open}
          data-testid="filter-toggle"
          data-control="text-link"
          onClick={() => setOpen((current) => !current)}
          sx={[
            LINK_SX,
            {
              color: "primary.main",
              fontWeight: 600,
              textDecoration: "underline",
              textUnderlineOffset: "2px",
            },
          ]}
        >
          {open ? t("filterClose") : t("filterOpen")}
          {active ? ` · ${activeCount}` : ""}
        </Box>
        {!open && summary !== "" && (
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
            {summary}
          </Typography>
        )}
      </Box>

      {open && (
        <Box
          data-testid="filter-panel"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            mt: 0.5,
            mb: 0.5,
            overflow: "hidden",
          }}
        >
          {rows.map((row, index) => (
            <Box key={row.key} sx={[ROW_SX, { borderTop: index === 0 ? 0 : 1 }]}>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>
                {row.label}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  columnGap: 1.5,
                  rowGap: 0.25,
                  alignItems: "center",
                }}
              >
                {row.values.length === 0 && (
                  <Typography variant="caption" color="text.disabled">
                    {t("filterNoneLeft")}
                  </Typography>
                )}
                {row.values.map((value) => (
                  <ValueLink
                    key={value.value}
                    label={value.label}
                    count={value.count}
                    selected={value.selected}
                    testId={value.testId ?? `${row.key}-${value.value || "none"}`}
                    onClick={() => row.onSelect(value.value)}
                  />
                ))}
              </Box>
            </Box>
          ))}

          {displayRows.length > 0 && (
            <>
              <Box sx={{ px: 1.25, pt: 0.75, pb: 0.25, borderTop: 1, borderColor: "divider" }}>
                <Typography variant="caption" color="text.secondary">
                  {t("filterDisplay")}
                </Typography>
              </Box>
              {displayRows.map((row) => (
                <Box key={row.key} sx={[ROW_SX, { borderTop: 0, py: 0.5 }]}>
                  <Typography variant="body2" sx={{ fontWeight: 650 }}>
                    {row.label}
                  </Typography>
                  <Box sx={{ display: "flex", columnGap: 1.5, alignItems: "center" }}>
                    <ValueLink
                      label={t("filterShow")}
                      selected={row.shown}
                      testId={`display-${row.key}-show`}
                      onClick={() => row.onChange(true)}
                    />
                    <ValueLink
                      label={t("filterHide")}
                      selected={!row.shown}
                      testId={`display-${row.key}-hide`}
                      onClick={() => row.onChange(false)}
                    />
                  </Box>
                </Box>
              ))}
            </>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 1.5,
              rowGap: 0.5,
              px: 1.25,
              py: 0.75,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Box
              component="button"
              type="button"
              disabled={!active}
              data-testid="filter-clear"
              data-control="text-link"
              onClick={onClear}
              sx={[
                LINK_SX,
                {
                  color: active ? "primary.main" : "text.disabled",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                },
              ]}
            >
              {t("filterClearAll")}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t("filterSemantics")}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function ValueLink({
  label,
  count,
  selected,
  testId,
  onClick,
}: {
  label: string;
  count?: number;
  selected: boolean;
  testId: string;
  onClick: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={selected}
      data-testid={testId}
      data-control="text-link"
      onClick={onClick}
      sx={[
        LINK_SX,
        {
          color: selected ? "primary.main" : "text.secondary",
          fontWeight: selected ? 700 : 400,
          textDecoration: "underline",
          textDecorationColor: selected ? "currentColor" : "divider",
          textUnderlineOffset: "2px",
          "&:hover": { color: "text.primary", textDecorationColor: "currentColor" },
        },
      ]}
    >
      {label}
      {count !== undefined && (
        <Box component="span" sx={{ ml: 0.5, color: "text.secondary", fontWeight: 400 }}>
          ({count})
        </Box>
      )}
    </Box>
  );
}

